'use client'

import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'

export type DateTimePickerProps = {
  locale?: string
  defaultValue?: Date
  value?: Date
  onChange?: (date?: Date) => void
  className?: string
  isInvalid?: boolean
}

const is24HourLocale = (locale?: string): boolean => {
  if (!locale) {
    return true // default 24h
  }
  try {
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit' }
    const formatted = new Date(2020, 0, 1, 13, 0).toLocaleTimeString(locale, options)
    return !/AM|PM|am|pm/.test(formatted)
  } catch {
    return true
  }
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  locale,
  defaultValue,
  value,
  onChange,
  className,
  isInvalid
}) => {
  const [date, setDate] = React.useState<Date | undefined>(value ?? defaultValue)
  const [isOpen, setIsOpen] = React.useState(false)

  React.useEffect(() => {
    if (value !== undefined) {
      setDate(value)
    }
  }, [value])

  const use24h = is24HourLocale(locale)

  const hours = React.useMemo(
    () => (use24h ? Array.from({ length: 24 }, (_, i) => i) : Array.from({ length: 12 }, (_, i) => i + 1)),
    [use24h]
  )

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // preserve time part
      if (date) {
        selectedDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0)
      }
      setDate(selectedDate)
      onChange?.(selectedDate)
    }
  }

  const handleTimeChange = (type: 'hour' | 'minute' | 'ampm', valueStr: string) => {
    if (!date) {
      return
    }
    const newDate = new Date(date)
    if (type === 'hour') {
      if (use24h) {
        newDate.setHours(parseInt(valueStr))
      } else {
        const val = parseInt(valueStr) % 12
        const isPM = newDate.getHours() >= 12
        newDate.setHours(val + (isPM ? 12 : 0))
      }
    } else if (type === 'minute') {
      newDate.setMinutes(parseInt(valueStr))
    } else if (type === 'ampm') {
      if (!use24h) {
        const currentHours = newDate.getHours()
        const targetPM = valueStr === 'PM'
        const isCurrentlyPM = currentHours >= 12
        if (targetPM !== isCurrentlyPM) {
          newDate.setHours((currentHours + 12) % 24)
        }
      }
    }
    setDate(newDate)
    onChange?.(newDate)
  }

  const formattedLabel = React.useMemo(() => {
    if (!date) {
      return use24h ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY hh:mm aa'
    }
    try {
      const optionsDate: Intl.DateTimeFormatOptions = { year: '2-digit', month: '2-digit', day: '2-digit' }
      const optionsTime: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: !use24h }
      const datePart = date.toLocaleDateString(locale || 'en-GB', optionsDate)
      const timePart = date.toLocaleTimeString(locale || (use24h ? 'en-GB' : 'en-US'), optionsTime)
      return `${datePart} ${timePart}`
    } catch {
      // fallback
      const pad = (n: number) => n.toString().padStart(2, '0')
      const mm = pad(date.getMonth() + 1)
      const dd = pad(date.getDate())
      const yyyy = date.getFullYear()
      const HH = pad(date.getHours())
      const hh = pad(((date.getHours() + 11) % 12) + 1)
      const mmn = pad(date.getMinutes())
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM'
      return use24h ? `${mm}/${dd}/${yyyy} ${HH}:${mmn}` : `${mm}/${dd}/${yyyy} ${hh}:${mmn} ${ampm}`
    }
  }, [date, locale, use24h])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            isInvalid && 'border-destructive focus-visible:ring-destructive',
            className
          )}>
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formattedLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="sm:flex">
          <Calendar mode="single" selected={date} onSelect={handleDateSelect} autoFocus />
          <div className="flex flex-col divide-y sm:h-75 sm:flex-row sm:divide-x sm:divide-y-0">
            <ScrollArea className="w-64 sm:w-auto">
              <div className="flex p-2 sm:flex-col">
                {hours
                  .slice()
                  .reverse()
                  .map(hour => (
                    <Button
                      key={hour}
                      size="icon"
                      variant={
                        date && (use24h ? date.getHours() === hour : date.getHours() % 12 === hour % 12)
                          ? 'default'
                          : 'ghost'
                      }
                      className="aspect-square shrink-0 sm:w-full"
                      onClick={() => handleTimeChange('hour', hour.toString())}>
                      {use24h ? hour.toString().padStart(2, '0') : hour}
                    </Button>
                  ))}
              </div>
              <ScrollBar orientation="horizontal" className="sm:hidden" />
            </ScrollArea>
            <ScrollArea className="w-64 sm:w-auto">
              <div className="flex p-2 sm:flex-col">
                {Array.from({ length: 12 }, (_, i) => i * 5).map(minute => (
                  <Button
                    key={minute}
                    size="icon"
                    variant={date && date.getMinutes() === minute ? 'default' : 'ghost'}
                    className="aspect-square shrink-0 sm:w-full"
                    onClick={() => handleTimeChange('minute', minute.toString())}>
                    {minute.toString().padStart(2, '0')}
                  </Button>
                ))}
              </div>
              <ScrollBar orientation="horizontal" className="sm:hidden" />
            </ScrollArea>
            {!use24h && (
              <ScrollArea>
                <div className="flex p-2 sm:flex-col">
                  {['AM', 'PM'].map(ampm => (
                    <Button
                      key={ampm}
                      size="icon"
                      variant={
                        date && ((ampm === 'AM' && date.getHours() < 12) || (ampm === 'PM' && date.getHours() >= 12))
                          ? 'default'
                          : 'ghost'
                      }
                      className="aspect-square shrink-0 sm:w-full"
                      onClick={() => handleTimeChange('ampm', ampm)}>
                      {ampm}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateTimePicker
